import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import {
  Badge, Popover, Button, ButtonSize,
} from '@usealloy/component-library';
import { formatSelectorSlug } from '../../helpers/formatting';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';


const TagWithTooltip = ({ triggeredTags, taggedResults, taggedGroup }) => {
  const [isOpen, setOpen] = useState(false);


  const displayTags = [];
  triggeredTags.forEach(tag => {
    if (!displayTags.some(t => t.customer_tag_id === tag.customer_tag_id
        && t.tag_name === tag.tag_name)) {
      displayTags.push(tag);
    }
  });

  const groupDisplayTags = displayTags.filter(tag => tag.customer_tag_id && Object.values(
    taggedResults[taggedGroup])[0][0].customer_tag_id.includes(tag.customer_tag_id),
  );

  if (groupDisplayTags.length < 5) {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
        {groupDisplayTags.map(tag => {
          const badgeId = `badge-${tag.customer_tag_id}`;
          return (
            <React.Fragment key={badgeId}>
              <Badge
                style={{ backgroundColor: `#${tag.tag_color}` }}
                id={badgeId}
              />
              <Tooltip
                placement="bottom"
                target={badgeId}
                modifiers={tooltipZIndexModifiers}
              >
                {tag.tag_name}
              </Tooltip>
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  return (
    <Popover
      trigger={(
        <Button
          variant="outline"
          size={ButtonSize.xsmall}
          style={{ padding: 'var(--cl-space-2)' }}
        >{groupDisplayTags.length}
        </Button>
)}
      onOpenChange={() => setOpen(!isOpen)}
      onClickOutside={() => {
        setOpen(false);
      }}
      onEscape={() => {
        setOpen(false);
      }}
      style={{ padding: 'var(--cl-space-4' }}
      side="bottom"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cl-space-4)' }}>
        {groupDisplayTags.map(tag => (
          <Badge
            key={tag.tag_name}
            style={{
              padding: 'var(--cl-space-4)', margin: '1px', width: '100%',
            }}
          >
            <Badge style={{ backgroundColor: `#${tag.tag_color}` }} />
            <span style={{ paddingLeft: '1rem' }}>{tag.tag_name}</span>
          </Badge>
        ))}
      </div>
    </Popover>
  );
};

TagWithTooltip.propTypes = {
  tag: PropTypes.shape({
    customer_tag_id: PropTypes.number.isRequired,
    tag_color: PropTypes.string.isRequired,
    tag_name: PropTypes.string.isRequired,
  }).isRequired,
  taggedResults: PropTypes.object.isRequired,
  taggedGroup: PropTypes.string.isRequired,
  triggeredTags: PropTypes.object.isRequired,
};

const ReturnedReasonCodesPropTypes = {
  extraneousReasonCodeResults: PropTypes.object.isRequired,
  taggedReasonCodeResults: PropTypes.object.isRequired,
  untaggedReasonCodeResults: PropTypes.object.isRequired,
  returnedTags: PropTypes.object.isRequired,
};

class ReturnedReasonCodes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      taggedReasonCodeToggle: {},
      untaggedReasonCodeToggle: {},
      extraneousReasonCodeToggle: false,
    };
    this.toggleDisplayedReasonCodes = this.toggleDisplayedReasonCodes.bind(this);
  }

  toggleDisplayedReasonCodes(toggleState) {
    this.setState(prevState => ({
      [toggleState]: !prevState[toggleState],
    }));
  }

  static displayNonextreanousReasonCodes(results, group) {
    const elements = [];
    const singleGroup = results[group];
    Object.entries(singleGroup).forEach(([service, reasonCode]) => {
      elements.push(
        <>
          <b>{service}</b>
          <hr />
        </>,
      );
      reasonCode.forEach(code => {
        elements.push(ReturnedReasonCodes.renderReasonCode(code));
      });
    });
    return elements;
  }

  static displayExtraneousReasonCodes(service, reasonCodes) {
    const elements = [];
    elements.push(
      <>
        <b>{service}</b>
        <hr />
      </>,
    );
    reasonCodes.forEach(code => {
      elements.push(ReturnedReasonCodes.renderReasonCode(code));
    });

    return elements;
  }

  static renderService(service) {
    return [...service]
      .sort((a, b) => a.code.localeCompare(b.code))
      .map(ReturnedReasonCodes.renderReasonCode);
  }

  static renderReasonCode(code) {
    return (
      <p key={code.code} className="grey">
        {code.code}
        {!code.description || code.code === code.description ? '' : ` - ${code.description}`}
      </p>
    );
  }

  static getTaggedThresholds(results, group, returnedTags) {
    const flattenedArr = Object.values(results[group]).flat();
    const tags = [];
    const untriggeredGroups = [];
    for (const obj of flattenedArr) {
      if (obj.tagged_thresholds && obj.tagged_thresholds.length > 0) {
        for (const threshold of obj.tagged_thresholds) {
          const existingTag = tags?.find(tag => tag.tag_name === threshold.tag_name);
          if (!existingTag) {
            tags.push(threshold);
          }
        }
      } else if (!untriggeredGroups.includes(group)) {
        untriggeredGroups.push(group);
      }
    }
    const filteredTags = tags.flat()
      .filter(tag => returnedTags.some(rTag => rTag.tag_name === tag.tag_name));
    const untriggeredTags = tags.flat()
      .filter(tag => !returnedTags.some(rTag => rTag.tag_name === tag.tag_name));

    const updatedResults = results;

    if (untriggeredTags.length) {
      Object.keys(updatedResults[group]).forEach(serviceName => {
        updatedResults[group][serviceName].map(
          serviceItem => {
            const newTaggedThresholds = serviceItem.tagged_thresholds.map(tag => ({
              ...tag,
            })).filter(tag => !untriggeredTags.some(
              untriggeredTag => untriggeredTag.tag_name === tag.tag_name
                && untriggeredTag.customer_tag_id === tag.customer_tag_id,
            ));

            return {
              ...serviceItem,
              tagged_thresholds: newTaggedThresholds,
            };
          },
        );
      });
    }
    return { filteredTags, untriggeredGroups };
  }


  render() {
    let { untaggedReasonCodeResults } = this.props;
    const { returnedTags, extraneousReasonCodeResults, taggedReasonCodeResults } = this.props;
    const { extraneousReasonCodeToggle } = this.state;
    const extraneousReasonCodeServices = extraneousReasonCodeResults
      ? Object.keys(extraneousReasonCodeResults) : [];
    let taggedReasonCodeGroups = taggedReasonCodeResults
      ? Object.keys(taggedReasonCodeResults) : [];

    const accumulatedData = taggedReasonCodeGroups.reduce(
      (acc, group) => {
        const { filteredTags, untriggeredGroups } = ReturnedReasonCodes.getTaggedThresholds(
          taggedReasonCodeResults,
          group,
          returnedTags,
        );

        acc.triggeredTags = [...acc.triggeredTags, ...filteredTags];

        acc.untriggeredGroups = [...acc.untriggeredGroups, ...untriggeredGroups];

        return acc;
      },
      { triggeredTags: [], untriggeredGroups: [] },
    );

    const { triggeredTags, untriggeredGroups } = accumulatedData;

    const reasonCodeGroupsToUntag = {};

    if (untriggeredGroups && untriggeredGroups.length > 0) {
      untriggeredGroups.forEach(group => {
        reasonCodeGroupsToUntag[group] = taggedReasonCodeResults[group];
      });
    }

    untaggedReasonCodeResults = { ...untaggedReasonCodeResults, ...reasonCodeGroupsToUntag };
    const untaggedReasonCodeGroups = untaggedReasonCodeResults
      ? Object.keys(untaggedReasonCodeResults) : [];

    taggedReasonCodeGroups = taggedReasonCodeGroups
      .filter(group => !untaggedReasonCodeGroups.includes(group));

    return (
      <div className="reason-codes-content" ref={this.targetRef}>
        {taggedReasonCodeGroups.map(taggedReasonCodeGroup => (
          <div key={taggedReasonCodeGroup} className="reason-codes-group header-8">
            <div className="reason-code-display">
              <p
                className="bold flex start header-8 reason-code-group-title"
                // eslint-disable-next-line react/destructuring-assignment
                data-target={this.state[`taggedReasonCodeToggle-${taggedReasonCodeGroup}`]}
                onClick={() => this.toggleDisplayedReasonCodes(`taggedReasonCodeToggle-${taggedReasonCodeGroup}`)}
              >
                <i
                  className={
                      // eslint-disable-next-line react/destructuring-assignment
                      `far fa-fw fa-lg fa-angle-${this.state[`taggedReasonCodeToggle-${taggedReasonCodeGroup}`] ? 'down' : 'right'}`
                    }
                  // eslint-disable-next-line react/destructuring-assignment
                  data-target={this.state[`taggedReasonCodeToggle-${taggedReasonCodeGroup}`]}
                />
                <span>
                  <u>
                    {taggedReasonCodeGroup} (
                    {Object.values(taggedReasonCodeResults[taggedReasonCodeGroup])
                      .reduce((acc, arr) => acc + arr.length, 0)}
                    )
                  </u>
                </span>
              </p>
              <TagWithTooltip
                triggeredTags={triggeredTags}
                taggedResults={taggedReasonCodeResults}
                taggedGroup={taggedReasonCodeGroup}
              />
            </div>
            <div className="group-services">
              {
              // eslint-disable-next-line react/destructuring-assignment
              this.state[`taggedReasonCodeToggle-${taggedReasonCodeGroup}`]
                && ReturnedReasonCodes.displayNonextreanousReasonCodes(
                  taggedReasonCodeResults,
                  taggedReasonCodeGroup,
                )
              }
            </div>
          </div>
        ))}

        {untaggedReasonCodeGroups.map(untaggedReasonCodeGroup => (
          <div key={untaggedReasonCodeGroup} className="reason-codes-group header-8">
            <div className="reason-code-display">
              <p
                className="bold flex start header-8 reason-code-group-title"
                // eslint-disable-next-line react/destructuring-assignment
                data-target={this.state[`untaggedReasonCodeToggle-${untaggedReasonCodeGroup}`]}
                onClick={() => this.toggleDisplayedReasonCodes(`untaggedReasonCodeToggle-${untaggedReasonCodeGroup}`)}
              >
                <i
                  className={
                    // eslint-disable-next-line react/destructuring-assignment
                    `far fa-fw fa-lg fa-angle-${this.state[`untaggedReasonCodeToggle-${untaggedReasonCodeGroup}`] ? 'down' : 'right'}`
                  }
                  // eslint-disable-next-line react/destructuring-assignment
                  data-target={this.state[`untaggedReasonCodeToggle-${untaggedReasonCodeGroup}`]}
                />
                <span>
                  <u>{untaggedReasonCodeGroup} ({(Object.values(
                    untaggedReasonCodeResults[untaggedReasonCodeGroup],
                  )
                    .filter(Array.isArray).reduce((acc, arr) => acc + arr.length, 0))})
                  </u>
                </span>
              </p>
              <i className="fa fa-info-circle" id={formatSelectorSlug('untaggedReasonCodeResults')} />
              <Tooltip
                placement="right-end"
                target={formatSelectorSlug('untaggedReasonCodeResults')}
                modifiers={tooltipZIndexModifiers}
              >
                This reason code group was returned
                but did not contribute to the result of the evaluation.
              </Tooltip>

            </div>
            <div className="group-services">
              {
              // eslint-disable-next-line react/destructuring-assignment
              this.state[`untaggedReasonCodeToggle-${untaggedReasonCodeGroup}`]
            && ReturnedReasonCodes.displayNonextreanousReasonCodes(
              untaggedReasonCodeResults,
              untaggedReasonCodeGroup,
            )
            }
            </div>
          </div>
        ),
        )}

        {extraneousReasonCodeServices.length > 0
          && (
          <div className="reason-code-display">
            <p
              className="bold flex start header-8 reason-code-group-title"
              data-target={extraneousReasonCodeToggle}
              onClick={() => this.toggleDisplayedReasonCodes('extraneousReasonCodeToggle')}
            >
              <i
                className={`far fa-fw fa-lg fa-angle-${extraneousReasonCodeToggle ? 'down' : 'right'}`}
                data-target={extraneousReasonCodeToggle}
              />
              <span>
                <u>Unclassified Reason Codes ({(Object.values(extraneousReasonCodeResults)
                  .filter(Array.isArray).reduce((acc, arr) => acc + arr.length, 0))})
                </u>
              </span>
            </p>

            <i className="fa fa-info-circle" id={formatSelectorSlug('extraneousReasonCodeResults')} />
            <Tooltip
              placement="right-end"
              target={formatSelectorSlug('extraneousReasonCodeResults')}
              modifiers={tooltipZIndexModifiers}
            >
              These reason codes were returned, but are not configured in a
              group and did not contribute to the outcome of the evaluation.
            </Tooltip>

          </div>
          )}
        {extraneousReasonCodeServices.map(extraneousReasonCodeService => (
          <div key={extraneousReasonCodeService} className="group-services">
            {extraneousReasonCodeToggle
                && ReturnedReasonCodes.displayExtraneousReasonCodes(
                  extraneousReasonCodeService,
                  extraneousReasonCodeResults[extraneousReasonCodeService])}
          </div>
        ))}
        {!taggedReasonCodeGroups.length && !untaggedReasonCodeGroups.length
        && !extraneousReasonCodeServices.length && (
        <p className="light-grey"><i>No reason codes were returned for this evaluation.</i></p>
        )}

      </div>

    );
  }
}
ReturnedReasonCodes.propTypes = ReturnedReasonCodesPropTypes;
export default ReturnedReasonCodes;

